/**
 * Segmentação de clientes (Task 13, brief 3.4 / D-13.5).
 *
 * O que esta tela faz de diferente de uma lista de etiquetas:
 *
 * 1. **O segmento é uma REGRA avaliada**, não uma lista salva de membros. A
 *    tela aplica `evaluateSegments` (regra pura) sobre os registros reais de
 *    cada cliente — pedidos unificados, conversas, orçamentos e estágio — então
 *    o segmento nunca discorda dos registros.
 * 2. **Três respostas, não duas.** Cliente aparece em "pertence", "não
 *    pertence" ou **"não avaliável"**, com o motivo: quando a regra depende de
 *    uma fonte que não respondeu, dizer "não pertence" faria o operador
 *    concluir que o cliente não compra.
 * 3. **Cada linha navega até o cliente** (visão 360), que é onde os registros
 *    que produziram o resultado estão.
 *
 * Redesign R6 (apresentação apenas): wrapper e header no padrão aprovado; cada
 * bloco `.segment-detail` vira cartão tokenizado com h3 `text-lg font-semibold
 * text-ink`; as notas `.page__note` recebem `text-sm text-muted`. Todos os
 * `data-*` (incl. `data-segment-key`, `data-segment-matched`,
 * `data-segment-unknown`, `data-segments-scope`, `data-segment-match`), os
 * textos e os links são PRESERVADOS — as classes hook seguem no markup.
 *
 * Migrado para `.tsx`/JSX real (Cluster C — Atendimento & CRM, redesign por
 * perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * (`createElement` → JSX) + ícone lucide (`Tag`) no título da tela e em cada
 * bloco de segmento, no mesmo padrão visual de `KpiCard.tsx` (ícone + texto,
 * nunca só cor) — esta tela não tinha nenhum ícone antes. Nenhum `data-*`/
 * texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tag } from "lucide-react";
import { Avatar, Badge } from "../ui/index.js";
import { RecordTable } from "../components/RecordTable.js";
import { CrmBoundary } from "../components/CrmBoundary.js";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { OrdersBoundary } from "../components/OrdersBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { isCrmSourceAwaitingContract, type CrmDataset } from "../crm/dataset.js";
import { buildCrm360 } from "../crm/customer360.js";
import { evaluateSegmentRule, parseSegmentRule, type SegmentMatch } from "../crm/segments.js";
import { AWAITING_CONTRACT_LABEL, type DashboardDataset } from "../dashboard/kpis.js";
import { formatBrl } from "../format.js";
import type { OrdersDataset } from "../orders/dataset.js";
import type { SegmentosViewProps } from "../crm/view-props.js";
import type { CrmSegment } from "../api/types.js";

interface SegmentRow {
  customerId: string;
  customerName: string;
  match: SegmentMatch;
  paidOrderCount: number | null;
  totalSpentCents: number;
}

const MATCH_LABELS: Record<SegmentMatch["state"], string> = {
  match: "pertence",
  no_match: "não pertence",
  unknown: "não avaliável"
};

/**
 * Avalia um segmento para cada cliente do cadastro, usando os MESMOS fatos que
 * a visão 360 calcula (`buildCrm360` → `facts`). Avaliar aqui com outra conta
 * produziria duas verdades sobre o mesmo cliente.
 */
export function evaluateSegmentRows(
  segment: CrmSegment,
  props: { crm: CrmDataset; dataset: DashboardDataset; orders?: OrdersDataset; now: Date }
): { rows: SegmentRow[]; ruleUnderstood: boolean } {
  const rule = parseSegmentRule(segment.rule);
  if (!rule) return { rows: [], ruleUnderstood: false };
  const rows: SegmentRow[] = [];
  for (const customer of props.crm.customers) {
    const view = buildCrm360({
      dataset: props.dataset,
      customerId: customer.id,
      auditEvents: null,
      orders: props.orders,
      crm: props.crm,
      now: props.now
    });
    if (!view) continue;
    rows.push({
      customerId: customer.id,
      customerName: view.customer.name,
      match: evaluateSegmentRule(rule, view.facts, props.now),
      paidOrderCount: view.facts.paidOrderCount,
      totalSpentCents: view.customer.totalSpentCents
    });
  }
  return { rows, ruleUnderstood: true };
}

function SegmentDetail({
  segment,
  rows,
  ruleUnderstood
}: {
  segment: CrmSegment;
  rows: SegmentRow[];
  ruleUnderstood: boolean;
}): ReactElement {
  const matched = rows.filter((row) => row.match.state === "match");
  const unknown = rows.filter((row) => row.match.state === "unknown");
  const outside = rows.length - matched.length - unknown.length;
  const pct = (n: number): string => (rows.length === 0 ? "0%" : `${Math.round((n / rows.length) * 100)}%`);
  return (
    <section
      className="segment-detail flex flex-col gap-4 rounded-panel border border-border bg-surface p-5 shadow-s1"
      data-segment-key={segment.key}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h3 className="flex items-center gap-2 text-lg font-extrabold text-ink">
            <Tag className="h-4 w-4 shrink-0 text-muted" aria-hidden focusable={false} />
            {segment.name}
          </h3>
          <p className="page__note m-0 text-sm text-muted">{segment.description || "Sem descrição registrada."}</p>
        </div>
        {ruleUnderstood ? <Badge tone={matched.length > 0 ? "brand" : "neutral"}>{`${matched.length} cliente(s)`}</Badge> : null}
      </div>
      {ruleUnderstood ? (
        <>
          <div aria-hidden className="flex h-2 overflow-hidden rounded-pill bg-surface-soft">
            <span className="bg-brand" style={{ width: pct(matched.length) }} />
            <span className="bg-border-strong" style={{ width: pct(unknown.length) }} />
          </div>
          <dl className="m-0 grid grid-cols-3 gap-3 text-center">
            <div>
              <dt className="text-xs font-extrabold text-muted">Pertencem</dt>
              <dd className="m-0 text-2xl font-black tabular-nums text-ink">{matched.length}</dd>
            </div>
            <div>
              <dt className="text-xs font-extrabold text-muted">Fora</dt>
              <dd className="m-0 text-2xl font-black tabular-nums text-ink">{outside}</dd>
            </div>
            <div>
              <dt className="text-xs font-extrabold text-muted">Não avaliáveis</dt>
              <dd className="m-0 text-2xl font-black tabular-nums text-ink">{unknown.length}</dd>
            </div>
          </dl>
          <p className="sr-only" data-segment-matched={matched.length} data-segment-unknown={unknown.length}>
            {`${matched.length} cliente(s) pertencem · ${unknown.length} não avaliável(is) por fonte ausente · ${outside} fora do segmento.`}
          </p>
          {matched.length > 0 ? (
            <ul className="m-0 flex list-none flex-wrap gap-2 p-0" aria-label="Clientes que pertencem ao segmento">
              {matched.map((row) => (
                <li key={row.customerId}>
                  <Link to={`/clientes/${row.customerId}`} className="inline-flex items-center gap-2 rounded-pill border border-border bg-surface-soft py-1 pl-1 pr-3 text-sm font-bold text-ink hover:border-ink">
                    <Avatar name={row.customerName} />
                    {row.customerName}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-sm text-muted">Nenhum cliente pertence a este segmento agora.</p>
          )}
        </>
      ) : (
        <StatusPanel
          kind="awaiting_source"
          title="Regra salva não reconhecida por esta versão da Plataforma"
          detail={`O segmento ${segment.key} tem uma regra que este código não sabe interpretar. Nenhum cliente é classificado por ela — avaliar como 'não pertence' esconderia a incompatibilidade.`}
        />
      )}
      {ruleUnderstood ? (
        <details className="group rounded-control border border-border">
          <summary className="cursor-pointer list-none px-4 py-3 text-sm font-extrabold text-ink hover:bg-surface-soft">Ver avaliação cliente a cliente</summary>
          <div className="border-t border-border">
            <RecordTable<SegmentRow>
              bare
              caption={`Clientes avaliados pela regra do segmento ${segment.key}`}
              rows={rows}
              idOf={(row) => row.customerId}
              emptyMessage="Nenhum cliente no cadastro para avaliar."
              columns={[
                {
                  key: "customer",
                  label: "Cliente",
                  render: (row) => <Link to={`/clientes/${row.customerId}`}>{row.customerName}</Link>
                },
                {
                  key: "match",
                  label: "Resultado",
                  render: (row) => (
                    <span data-segment-match={row.match.state}>
                      {row.match.state === "unknown" ? `${MATCH_LABELS.unknown} — ${row.match.reason}` : MATCH_LABELS[row.match.state]}
                    </span>
                  )
                },
                {
                  key: "orders",
                  label: "Pedidos contados",
                  render: (row) => (row.paidOrderCount === null ? <span data-pending="true">fonte de pedidos ausente</span> : String(row.paidOrderCount))
                },
                { key: "spent", label: "Total gasto", render: (row) => formatBrl(row.totalSpentCents) }
              ]}
            />
          </div>
        </details>
      ) : null}
    </section>
  );
}

export function SegmentosView({ crm, dataset, orders, now, segmentKey = null }: SegmentosViewProps): ReactElement {
  if (isCrmSourceAwaitingContract(crm, "segments")) {
    return (
      <div className="page page--segmentos flex flex-col gap-6" data-page="segmentos">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">Segmentos</h2>
        </header>
        <StatusPanel
          kind="awaiting_source"
          title={`Segmentação ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/crm/segments não respondeu neste ambiente. Sem as regras salvas não há segmentação a avaliar."
        />
      </div>
    );
  }
  const instant = now ?? new Date();
  const segments = crm.segments.filter((segment) => segmentKey === null || segment.key === segmentKey);
  return (
    <div className="page page--segmentos flex flex-col gap-6" data-page="segmentos">
      <header className="drilldown-header flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">Segmentos</h2>
          <p className="drilldown-header__origin mt-1 text-base text-muted" data-segments-scope={segmentKey ?? "all"}>
            {`${segments.length} segmento(s) neste recorte, de ${crm.segments.length} cadastrado(s).`}
          </p>
        </div>
        {segmentKey ? (
          <Link to="/crm/segmentos" className="drilldown-header__back text-sm font-bold text-brand-text underline">
            Ver todos os segmentos
          </Link>
        ) : null}
      </header>
      <p className="page__note m-0 text-sm text-muted">
        Segmento é regra salva avaliada sobre os registros reais a cada leitura, não lista de membros gravada:
        cliente que voltou a comprar sai do segmento de inativos no mesmo instante.
      </p>
      <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
      {segments.map((segment) => {
        const evaluated = evaluateSegmentRows(segment, { crm, dataset, orders, now: instant });
        return <SegmentDetail key={segment.key} segment={segment} rows={evaluated.rows} ruleUnderstood={evaluated.ruleUnderstood} />;
      })}
      </div>
    </div>
  );
}

export function Segmentos(): ReactElement {
  const [searchParams] = useSearchParams();
  const segmentKey = searchParams.get("segmento");
  return (
    <DatasetBoundary>
      {(dataset: DashboardDataset) => (
        <OrdersBoundary>
          {(orders: OrdersDataset) => (
            <CrmBoundary>{(crm: CrmDataset) => <SegmentosView crm={crm} dataset={dataset} orders={orders} segmentKey={segmentKey} />}</CrmBoundary>
          )}
        </OrdersBoundary>
      )}
    </DatasetBoundary>
  );
}
